<template>
  <div class="mall-player" :class="theme">
    <video :src="source | replaceUrlProtocol" ref="player" v-if="theme === 'video'" playsinline></video>
    <audio :src="source | replaceUrlProtocol" ref="player" v-else></audio>
  </div>
</template>

<script>
import Plyr from 'plyr/dist/plyr.mjs';
import 'plyr/src/sass/plyr.scss?no-px2rem';
import { trackElClickH5 } from '@changba/funny';

export default {
  name: 'MallPlayer',

  props: {
    src: {
      type: [String, Array],
      default: ''
    },

    theme: {
      type: String,
      default: 'before'
    }
  },

  data() {
    return {
      source: '',
      currentTime: 0
    };
  },

  watch: {
    src(value) {
      this.stop();
      this.source = typeof value === 'string' ? value : value[0];
    }
  },

  mounted() {
    this.player = new Plyr(this.$refs.player, {
      controls: ['play-large', 'play', 'progress', 'current-time', 'duration'],
      iconUrl: '//res.cdn.changbaimg.com/-/45328fbd01e8be57/plyr.svg',
      blankVideo: '//res.cdn.changbaimg.com/-/a251e59b016e90e2/blank.mp4'
    });

    this.player.on('play', () => {
      this.$emit('play');
      trackElClickH5('播放器播放');
    });
    this.player.on('pause', () => {
      this.$emit('stop');
      trackElClickH5('播放器暂停');
    });

    this.$on('switch', this.handleSwitch);
    this.$on('get-tag', this.handleTag);

    if (this.src) {
      this.source = typeof this.src === 'string' ? this.src : this.src[0];
    }
  },

  methods: {
    handleSwitch(index) {
      const savePlayingStatus = this.player.playing;

      this.currentTime = this.player.currentTime;
      this.player.stop();

      this.player.source = {
        type: 'audio',
        sources: [{ src: this.src[index], type: 'audio/mp3' }]
      };

      this.player.once('canplaythrough', () => {
        setTimeout(() => {
          this.player.currentTime = this.currentTime;

          if (savePlayingStatus) {
            this.player.play();
          }
        }, 100);
      });
    },

    handleTag() {
      this.player.pause();

      const time = Math.floor(this.player.currentTime);
      const minutes = Math.floor(time / 60);
      const seconds = time - minutes * 60;

      this.$emit('set-tag', `${minutes}:${seconds}`);
    },

    stop() {
      this.player.stop();
    },

    play() {
      this.$nextTick(() => {
        this.player.play();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@cdn_css/helper.scss";

.mall-player {
  position: relative;
  width: 100%;
  background: #f5f5f5;
  border-radius: 8px;

  &.video {
    height: 100%;
  }

  &::v-deep {
    .plyr__controls {
      .plyr__control {
        svg {
          use {
            display: none;
          }
        }
      }
    }

    .plyr--audio {
      .plyr__controls {
        padding-top: 9px;
        padding-right: 10px;
        padding-bottom: 9px;
        padding-left: 12px;
        background-color: transparent;

        .plyr__progress {
          input[type=range] {
            color: #ffb358;
          }

          .plyr__progress__buffer {
            color: #d8d8d8;
          }
        }

        .plyr__control {
          padding: 0;

          &.plyr__tab-focus,
          &:hover {
            background: transparent;
            box-shadow: none;
          }

          .icon--not-pressed {
            width: 30px;
            height: 30px;
            -svg-mixer-fill: #fff path, #ffb358 circle;
            @include bg-img("~@cdn_img/play.svg", contain);
          }

          .icon--pressed {
            width: 30px;
            height: 30px;
            -svg-mixer-fill: #fff g, #ffb358 circle;
            @include bg-img("~@cdn_img/stop.svg", contain);
          }
        }
      }
    }

    .plyr--video {
      .plyr__controls {
        .plyr__progress__container {
          margin-left: 10px;

          .plyr__progress {
            input[type=range] {
              color: #ff655a;
            }
          }
        }

        .plyr__control {
          margin-left: 10px;
          padding: 0;

          &.plyr__tab-focus,
          &:hover {
            background: transparent;
            box-shadow: none;
          }

          .icon--not-pressed {
            width: 18px;
            height: 18px;
            @include bg-img("~@cdn_img/play-cool.svg", contain);
          }

          .icon--pressed {
            width: 18px;
            height: 18px;
            @include bg-img("~@cdn_img/stop-cool.svg", contain);
          }
        }
      }
    }
  }

  &.after {
    &::v-deep {
      .plyr--audio {
        .plyr__controls {
          .plyr__progress {
            input[type=range] {
              color: #ff655a;
            }
          }

          .plyr__control {
            .icon--not-pressed {
              -svg-mixer-fill: #fff path, #ff655a circle;
              @include bg-img("~@cdn_img/play.svg", contain);
            }

            .icon--pressed {
              -svg-mixer-fill: #fff g, #ff655a circle;
              @include bg-img("~@cdn_img/stop.svg", contain);
            }
          }
        }
      }
    }
  }

  &.cool {
    &::v-deep {
      .plyr--audio {
        .plyr__controls {
          padding: 0;

          .plyr__progress {
            margin-left: 0;

            input[type=range] {
              color: #121212;

              &.plyr__tab-focus::-webkit-slider-runnable-track,
              &.plyr__tab-focus::-webkit-slider-thumb {
                box-shadow: none;
              }

              &::-webkit-slider-runnable-track {
                height: 4px;
                background-color: #ccc;
              }

              &::-webkit-slider-thumb {
                width: 9px;
                height: 9px;
                margin-top: -2.5px;
                background: #121212;
              }
            }

            .plyr__progress__buffer {
              height: 1px;
              margin-top: -0.5px;
            }
          }

          .plyr__control {
            display: none;
          }

          .plyr__time {
            padding-right: 0;
            padding-left: 10px;
            color: #121212;
            font-size: 10px;
            font-family: DINAlternate-Bold;

            + .plyr__time {
              margin-left: 0;
              padding-left: 0;

              &:before {
                margin-right: 0;
                content: '/';
              }
            }

            @media (max-width: 767px) {
              + .plyr__time {
                display: block;
              }
            }
          }
        }
      }
    }
  }
}
</style>
